import { useState, useEffect, useRef, useCallback } from "react";
import { Play, Pause, Volume2, VolumeX, X, RotateCcw, Download, Mic, Loader2, Video } from "lucide-react";
import { IMAGES, recordVideo, createBackgroundMusic } from "./presentation/videoUtils";

const API = process.env.REACT_APP_BACKEND_URL;

const ease = (p, start, speed) => Math.min(Math.max((p - start) * speed, 0), 1);
const slideIn = (p, start, speed) => `translateX(${(1 - ease(p, start, speed)) * -30}px)`;

const SLIDES = [
  {
    id: "opening", duration: 13000, type: "text",
    bg: "linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 40%, #dbeafe 100%)",
    content: (p) => (
      <div className="flex flex-col items-center justify-center h-full text-center px-8 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 30% 40%, #93c5fd 0%, transparent 50%), radial-gradient(circle at 70% 60%, #67e8f9 0%, transparent 50%)" }} />
        <img src="/cmes-logo.png" alt="CMES" className="w-32 h-32 md:w-40 md:h-40 mb-6 object-contain rounded-2xl shadow-2xl"
          style={{ opacity: ease(p, 0, 5), transform: `scale(${0.6 + ease(p, 0, 3) * 0.4})` }} />
        <h1 className="text-5xl md:text-7xl font-black mb-3 tracking-tight" style={{ opacity: ease(p, 0.06, 5), color: "#0c4a6e" }}>CMES</h1>
        <div className="h-1.5 bg-gradient-to-r from-transparent via-sky-500 to-transparent mb-5 rounded-full" style={{ width: `${ease(p, 0.1, 4) * 320}px`, opacity: ease(p, 0.1, 4) }} />
        <p className="text-xl md:text-3xl font-semibold max-w-xl" style={{ opacity: ease(p, 0.16, 5), color: "#0369a1" }}>La pharmacie d'officine m&eacute;rite mieux</p>
        <div className="flex gap-4 mt-6" style={{ opacity: ease(p, 0.25, 4) }}>
          {[
            { label: "PHARMA PÉRIMÉS", color: "#00C78F" },
            { label: "PHARMA ASSURANCES", color: "#3B82F6" },
            { label: "PHARMA DISPENSATION", color: "#8B5CF6" },
          ].map((s) => (
            <span key={s.label} className="px-3 py-1.5 rounded-full text-xs font-bold text-white" style={{ background: s.color }}>{s.label}</span>
          ))}
        </div>
        <p className="text-sm md:text-base font-bold mt-5 tracking-[0.2em] uppercase" style={{ opacity: ease(p, 0.3, 4), color: "#334155" }}>Coaching M&eacute;dical et &Eacute;panouissement Social</p>
      </div>
    ),
  },
  {
    id: "scene_pharmacy", duration: 14000, type: "image", imageSrc: IMAGES.pharmacy,
    content: (p) => (
      <div className="flex flex-col justify-end h-full p-8 pb-24 md:p-14 md:pb-28 relative">
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/35" />
        <div className="relative z-10 max-w-2xl" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.7)', color: '#ffffff' }}>
          <p className="text-xs uppercase tracking-[0.3em] mb-3 font-black text-sky-300" style={{ opacity: ease(p, 0, 6) }}>Le quotidien des officines</p>
          <h2 className="text-3xl md:text-5xl font-black mb-4 leading-tight text-white" style={{ opacity: ease(p, 0.03, 5) }}>
            Entre patients et factures,<br/><span className="text-sky-300">le temps manque cruellement</span>
          </h2>
          <p className="text-base md:text-lg text-white max-w-xl leading-relaxed" style={{ opacity: ease(p, 0.1, 4) }}>
            Votre m&eacute;tier, c'est soigner. Pas g&eacute;rer de la paperasse. Pourtant, entre les stocks, les factures d'assurance et les p&eacute;rim&eacute;s, le quotidien est un combat.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "pain_combined", duration: 15000, type: "image", imageSrc: IMAGES.papers,
    content: (p) => (
      <div className="flex flex-col justify-center h-full p-8 md:p-16 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-red-950/95 via-red-900/85 to-red-900/60" />
        <div className="max-w-2xl relative z-10" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.7)', color: '#ffffff' }}>
          <p className="text-xs uppercase tracking-[0.3em] mb-4 font-black text-red-300" style={{ opacity: ease(p, 0, 6) }}>Le constat</p>
          <h2 className="text-3xl md:text-5xl font-black mb-5 leading-tight text-white" style={{ opacity: ease(p, 0.03, 5) }}>
            Des millions de FCFA<br/><span className="text-red-300">perdus chaque mois</span>
          </h2>
          <div className="space-y-4 mt-4">
            {["\u2022 Factures \u00e9gar\u00e9es, relances sans r\u00e9ponse", "\u2022 Retards de paiement non d\u00e9tect\u00e9s", "\u2022 P\u00e9rim\u00e9s non trac\u00e9s, stocks inconnus", "\u2022 Des heures perdues en gestion manuelle"].map((item, i) => (
              <p key={i} className="text-base md:text-lg font-semibold text-white"
                style={{ opacity: ease(p, 0.08 + i * 0.05, 5), transform: slideIn(p, 0.08 + i * 0.05, 4) }}>{item}</p>
            ))}
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "solution_intro", duration: 14000, type: "image", imageSrc: IMAGES.dashboard,
    content: (p) => (
      <div className="flex flex-col items-center justify-center h-full px-8 text-center relative">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/90 via-emerald-900/80 to-emerald-950/90" />
        <div className="relative z-10 max-w-2xl" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.7)', color: '#ffffff' }}>
          <p className="text-xs uppercase tracking-[0.3em] mb-4 font-black text-emerald-300" style={{ opacity: ease(p, 0, 6) }}>La solution</p>
          <h2 className="text-3xl md:text-5xl font-black mb-5 leading-tight text-white" style={{ opacity: ease(p, 0.03, 5) }}>
            CMES transforme<br/><span className="text-emerald-300">votre quotidien</span>
          </h2>
          <p className="text-lg md:text-xl text-white max-w-lg mx-auto leading-relaxed" style={{ opacity: ease(p, 0.1, 4) }}>
            Trois services compl&eacute;mentaires pour automatiser la gestion compl&egrave;te de votre officine.
          </p>
          <div className="flex gap-4 mt-6 justify-center" style={{ opacity: ease(p, 0.2, 4) }}>
            <span className="px-3 py-1.5 rounded-full text-xs font-bold text-white bg-blue-500">PHARMA ASSURANCES</span>
            <span className="px-3 py-1.5 rounded-full text-xs font-bold text-white bg-emerald-500">PHARMA PÉRIMÉS</span>
            <span className="px-3 py-1.5 rounded-full text-xs font-bold text-white bg-violet-500">PHARMA DISPENSATION</span>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "cmes_log", duration: 16000, type: "text",
    bg: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 40%, #f0f9ff 100%)",
    content: (p) => (
      <div className="flex flex-col items-center justify-center h-full px-6 text-center relative overflow-hidden">
        <div className="flex items-center gap-3 mb-6" style={{ opacity: ease(p, 0, 6) }}>
          <span className="px-3 py-1 rounded-full text-xs font-bold text-white bg-blue-500">SERVICE 1</span>
        </div>
        <h2 className="text-2xl md:text-4xl font-black mb-8" style={{ opacity: ease(p, 0.02, 6), color: "#1e293b" }}>
          <span style={{ color: "#2563eb" }}>PHARMA ASSURANCES</span> — Facturation & Recouvrement
        </h2>
        <div className="grid grid-cols-2 gap-4 md:gap-6 max-w-2xl w-full">
          {[
            { num: "01", title: "Scan OCR Intelligent", desc: "Num\u00e9risez factures et ch\u00e8ques automatiquement", accent: "#0369a1", border: "border-sky-400" },
            { num: "02", title: "Pipeline Kanban", desc: "6 \u00e9tapes visuelles du d\u00e9p\u00f4t au paiement", accent: "#0e7490", border: "border-cyan-400" },
            { num: "03", title: "Alertes Retards", desc: "D\u00e9tection et relances automatiques", accent: "#b45309", border: "border-amber-400" },
            { num: "04", title: "Pr\u00e9visions IA", desc: "Anticipez votre tr\u00e9sorerie", accent: "#047857", border: "border-emerald-400" },
          ].map((f, i) => (
            <div key={i} className={`bg-white border-2 ${f.border} rounded-2xl p-5 shadow-lg text-left`}
              style={{ opacity: ease(p, 0.05 + i * 0.04, 6), transform: `translateY(${(1 - ease(p, 0.05 + i * 0.04, 5)) * 20}px)` }}>
              <span className="text-lg font-black" style={{ color: f.accent }}>{f.num}</span>
              <p className="text-sm md:text-base font-bold mt-1" style={{ color: "#1e293b" }}>{f.title}</p>
              <p className="text-xs md:text-sm mt-1 leading-snug" style={{ color: "#475569" }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: "logistics", duration: 14000, type: "image", imageSrc: IMAGES.courier,
    content: (p) => (
      <div className="flex flex-col justify-end h-full p-8 pb-24 md:p-14 md:pb-28 relative">
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-900/80 to-slate-900/50" />
        <div className="max-w-2xl relative z-10" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.7)', color: '#ffffff' }}>
          <p className="text-xs uppercase tracking-[0.3em] mb-3 font-black text-cyan-300" style={{ opacity: ease(p, 0, 6) }}>Logistique int&eacute;gr&eacute;e</p>
          <h2 className="text-3xl md:text-5xl font-black mb-5 leading-tight text-white" style={{ opacity: ease(p, 0.03, 5) }}>
            Suivi de bout en bout<br/><span className="text-cyan-300">de vos agents commerciaux</span>
          </h2>
          <div className="space-y-3 mt-3">
            {["\u2713 GPS temps r\u00e9el de chaque course", "\u2713 Preuves photo et signature num\u00e9rique", "\u2713 Tra\u00e7abilit\u00e9 compl\u00e8te des factures", "\u2713 Messagerie assureurs int\u00e9gr\u00e9e"].map((item, i) => (
              <p key={i} className="text-sm md:text-base font-semibold text-white"
                style={{ opacity: ease(p, 0.08 + i * 0.05, 5), transform: slideIn(p, 0.08 + i * 0.05, 4) }}>{item}</p>
            ))}
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "intel_stock_intro", duration: 16000, type: "image", imageSrc: IMAGES.stockshelves,
    content: (p) => (
      <div className="flex flex-col items-center justify-center h-full px-8 text-center relative">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/92 via-emerald-900/85 to-teal-950/92" />
        <div className="relative z-10 max-w-2xl" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.7)', color: '#ffffff' }}>
          <div className="flex items-center gap-3 justify-center mb-4" style={{ opacity: ease(p, 0, 6) }}>
            <span className="px-3 py-1 rounded-full text-xs font-bold text-white bg-emerald-500">SERVICE 2</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-black mb-5 leading-tight text-white" style={{ opacity: ease(p, 0.03, 5) }}>
            <span className="text-emerald-300">PHARMA PÉRIMÉS</span><br/>Gestion des stocks & p&eacute;rim&eacute;s
          </h2>
          <div className="space-y-3 mt-4">
            {["\u2713 Alertes p\u00e9remption intelligentes (1, 2, 3, 6 mois)", "\u2713 Destructions s\u00e9curis\u00e9es avec QR code", "\u2713 Attestations de destruction certifi\u00e9es", "\u2713 Import CSV, API Caisse et scanner IA"].map((item, i) => (
              <p key={i} className="text-sm md:text-base font-semibold text-white"
                style={{ opacity: ease(p, 0.08 + i * 0.05, 5), transform: slideIn(p, 0.08 + i * 0.05, 4) }}>{item}</p>
            ))}
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "intel_stock_features", duration: 16000, type: "image", imageSrc: IMAGES.medicines,
    content: (p) => (
      <div className="flex flex-col items-center justify-center h-full px-8 text-center relative">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-950/90 via-orange-900/82 to-amber-950/90" />
        <div className="relative z-10 max-w-2xl" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.7)', color: '#ffffff' }}>
          <p className="text-xs uppercase tracking-[0.3em] mb-4 font-black text-amber-300" style={{ opacity: ease(p, 0, 6) }}>PHARMA PÉRIMÉS — Analyses</p>
          <h2 className="text-3xl md:text-5xl font-black mb-5 leading-tight text-white" style={{ opacity: ease(p, 0.03, 5) }}>
            Des analyses avanc&eacute;es<br/><span className="text-amber-300">pour votre stock</span>
          </h2>
          <div className="space-y-3 mt-4">
            {["\u2713 Cartographie de tonnage C\u00f4te d'Ivoire", "\u2713 D\u00e9tection des invendus (>90 jours sans vente)", "\u2713 Indicateurs ITPP et PharmExchange", "\u2713 Recommandations IA personnalis\u00e9es"].map((item, i) => (
              <p key={i} className="text-sm md:text-base font-semibold text-white"
                style={{ opacity: ease(p, 0.08 + i * 0.05, 5), transform: slideIn(p, 0.08 + i * 0.05, 4) }}>{item}</p>
            ))}
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "techconseil_intro", duration: 16000, type: "image", imageSrc: IMAGES.aibrain,
    content: (p) => (
      <div className="flex flex-col items-center justify-center h-full px-8 text-center relative">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-950/92 via-purple-900/85 to-violet-950/92" />
        <div className="relative z-10 max-w-2xl" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.7)', color: '#ffffff' }}>
          <div className="flex items-center gap-3 justify-center mb-4" style={{ opacity: ease(p, 0, 6) }}>
            <span className="px-3 py-1 rounded-full text-xs font-bold text-white bg-violet-500">SERVICE 3</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-black mb-5 leading-tight text-white" style={{ opacity: ease(p, 0.03, 5) }}>
            L'intelligence artificielle<br/><span className="text-purple-300">au service du pharmacien</span>
          </h2>
          <div className="space-y-3 mt-4">
            {["\u2713 Copilot IA : analyse d'ordonnances en temps r\u00e9el", "\u2713 D\u00e9tection d'interactions m\u00e9dicamenteuses", "\u2713 Profil Patient Virtuel (Digital Twin)", "\u2713 Google du M\u00e9dicament africain"].map((item, i) => (
              <p key={i} className="text-sm md:text-base font-semibold text-white"
                style={{ opacity: ease(p, 0.08 + i * 0.05, 5), transform: slideIn(p, 0.08 + i * 0.05, 4) }}>{item}</p>
            ))}
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "techconseil_features", duration: 16000, type: "text",
    bg: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 40%, #faf5ff 100%)",
    content: (p) => (
      <div className="flex flex-col items-center justify-center h-full px-6 text-center relative overflow-hidden">
        <h2 className="text-2xl md:text-3xl font-black mb-8" style={{ opacity: ease(p, 0, 6), color: "#1e293b" }}>
          <span style={{ color: "#7c3aed" }}>PHARMA DISPENSATION</span> — Modules IA
        </h2>
        <div className="grid grid-cols-2 gap-4 md:gap-6 max-w-2xl w-full">
          {[
            { num: "01", title: "Copilot Pharmacien", desc: "Analyse d'ordonnances avec IA", accent: "#7c3aed", border: "border-violet-400" },
            { num: "02", title: "Digital Twin", desc: "Profil Patient Virtuel avec historique", accent: "#9333ea", border: "border-purple-400" },
            { num: "03", title: "Pharmacovigilance", desc: "Alertes grossesse, interactions, effets", accent: "#a855f7", border: "border-fuchsia-400" },
            { num: "04", title: "Score Qualit\u00e9", desc: "Suivi et am\u00e9lioration de votre officine", accent: "#6d28d9", border: "border-indigo-400" },
          ].map((f, i) => (
            <div key={i} className={`bg-white border-2 ${f.border} rounded-2xl p-5 shadow-lg text-left`}
              style={{ opacity: ease(p, 0.03 + i * 0.04, 6), transform: `translateY(${(1 - ease(p, 0.03 + i * 0.04, 5)) * 20}px)` }}>
              <span className="text-lg font-black" style={{ color: f.accent }}>{f.num}</span>
              <p className="text-sm md:text-base font-bold mt-1" style={{ color: "#1e293b" }}>{f.title}</p>
              <p className="text-xs md:text-sm mt-1 leading-snug" style={{ color: "#475569" }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: "formules", duration: 16000, type: "text",
    bg: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 40%, #fef9c3 100%)",
    content: (p) => (
      <div className="flex flex-col items-center justify-center h-full px-6 text-center relative overflow-hidden">
        <h2 className="text-2xl md:text-4xl font-black mb-6" style={{ opacity: ease(p, 0, 6), color: "#1e293b" }}>
          Des <span style={{ color: "#d97706" }}>formules adapt&eacute;es</span> &agrave; vos besoins
        </h2>
        <div className="grid grid-cols-3 gap-4 max-w-3xl w-full">
          {[
            { title: "Individuel", desc: "Un seul service au choix", items: ["PHARMA ASSURANCES", "PHARMA PÉRIMÉS", "PHARMA DISPENSATION"], color: "#0369a1", bg: "bg-sky-50", border: "border-sky-300" },
            { title: "Combo", desc: "2 services avec remise", items: ["LOG + STOCK", "LOG + TECH", "STOCK + TECH"], color: "#059669", bg: "bg-emerald-50", border: "border-emerald-300" },
            { title: "Excellence", desc: "Les 3 services CMES", items: ["Tout inclus", "Support VIP", "Acc\u00e8s illimit\u00e9"], color: "#d97706", bg: "bg-amber-50", border: "border-amber-400" },
          ].map((cat, i) => (
            <div key={i} className={`${cat.bg} border-2 ${cat.border} rounded-2xl p-5 shadow-lg text-left`}
              style={{ opacity: ease(p, 0.04 + i * 0.06, 5), transform: `translateY(${(1 - ease(p, 0.04 + i * 0.06, 4)) * 30}px)` }}>
              <p className="text-lg font-black mb-1" style={{ color: cat.color }}>{cat.title}</p>
              <p className="text-xs mb-3" style={{ color: "#64748b" }}>{cat.desc}</p>
              {cat.items.map((it, j) => (
                <p key={j} className="text-xs font-semibold mb-1 flex items-center gap-1.5" style={{ color: "#334155" }}>
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: cat.color }} /> {it}
                </p>
              ))}
            </div>
          ))}
        </div>
        <p className="text-sm font-semibold mt-6" style={{ opacity: ease(p, 0.3, 4), color: "#92400e" }}>
          Essai gratuit de 30 jours — Aucun engagement
        </p>
      </div>
    ),
  },
  {
    id: "partnership", duration: 13000, type: "image", imageSrc: IMAGES.handshake,
    content: (p) => (
      <div className="flex flex-col items-center justify-center h-full px-8 text-center relative">
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-950/90 via-indigo-900/80 to-indigo-950/90" />
        <div className="relative z-10 max-w-2xl" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.7)', color: '#ffffff' }}>
          <p className="text-xs uppercase tracking-[0.3em] mb-4 font-black text-indigo-300" style={{ opacity: ease(p, 0, 6) }}>Portail B2B</p>
          <h2 className="text-3xl md:text-5xl font-black mb-5 leading-tight text-white" style={{ opacity: ease(p, 0.03, 5) }}>
            Connect&eacute; directement<br/><span className="text-indigo-300">aux assureurs</span>
          </h2>
          <p className="text-base md:text-lg text-white max-w-lg mx-auto leading-relaxed" style={{ opacity: ease(p, 0.1, 4) }}>
            Messagerie int&eacute;gr&eacute;e, r&eacute;conciliation automatique des paiements, et transparence totale sur chaque transaction.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "closing", duration: 17000, type: "image", imageSrc: IMAGES.team,
    content: (p) => (
      <div className="flex flex-col items-center justify-center h-full px-8 text-center relative">
        <div className="absolute inset-0 bg-gradient-to-b from-sky-950/90 via-sky-900/80 to-sky-950/92" />
        <div className="relative z-10" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.7)', color: '#ffffff' }}>
          <img src="/cmes-logo.png" alt="CMES" className="w-24 h-24 md:w-28 md:h-28 mb-5 object-contain rounded-2xl shadow-xl mx-auto"
            style={{ opacity: ease(p, 0, 5) }} />
          <h2 className="text-2xl md:text-4xl font-black mb-2 text-white" style={{ opacity: ease(p, 0.03, 5) }}>Dr KOBOU DIDIER</h2>
          <p className="text-base md:text-lg font-bold mb-1 text-sky-300" style={{ opacity: ease(p, 0.06, 5) }}>Pharmacien G&eacute;rant</p>
          <p className="text-xs font-bold mb-6 tracking-[0.15em] uppercase text-white/70" style={{ opacity: ease(p, 0.1, 5) }}>
            CMES — Coaching M&eacute;dical et &Eacute;panouissement Social
          </p>
          <div className="h-px w-28 bg-gradient-to-r from-transparent via-sky-400 to-transparent mb-6 mx-auto" style={{ opacity: ease(p, 0.13, 5) }} />
          <p className="text-xl md:text-3xl font-black mb-2 text-white" style={{ opacity: ease(p, 0.18, 4) }}>
            Trois services pour les pharmacies<br/><span className="text-sky-300">qui choisissent l'excellence</span>
          </p>
          <div className="flex justify-center gap-3 mt-4" style={{ opacity: ease(p, 0.24, 4) }}>
            <span className="px-3 py-1 rounded-full text-xs font-bold text-white bg-emerald-500">PHARMA PÉRIMÉS</span>
            <span className="px-3 py-1 rounded-full text-xs font-bold text-white bg-blue-500">PHARMA ASSURANCES</span>
            <span className="px-3 py-1 rounded-full text-xs font-bold text-white bg-violet-500">PHARMA DISPENSATION</span>
          </div>
          <p className="text-sm font-semibold mt-4 tracking-wide text-white/80" style={{ opacity: ease(p, 0.28, 4) }}>
            contact@cmes-pharma.org | 07 68 12 70 00 | Abidjan, C&ocirc;te d'Ivoire
          </p>
          <div className="mt-6" style={{ opacity: ease(p, 0.34, 4), transform: `scale(${0.85 + ease(p, 0.34, 3) * 0.15})` }}>
            <div className="bg-gradient-to-r from-sky-500 to-emerald-500 text-white px-12 py-4 rounded-xl text-lg font-bold shadow-lg shadow-sky-500/30 animate-pulse">
              Commencez maintenant
            </div>
          </div>
        </div>
      </div>
    ),
  },
];

export default function PresentationVideo({ onClose, autoPlay = true, inline = false, showDownload = false }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [audioReady, setAudioReady] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [slideDurations, setSlideDurations] = useState(() => SLIDES.map(s => s.duration));
  const [isRecording, setIsRecording] = useState(false);
  const [recordProgress, setRecordProgress] = useState(null);
  const [serverRender, setServerRender] = useState({ status: "idle", progress: 0, phase: "" });
  const autoStartDone = useRef(false);
  const animRef = useRef(null);
  const startTimeRef = useRef(null);
  const slideAudioRefs = useRef({});
  const bgmCtxRef = useRef(null);
  const bgmNodesRef = useRef(null);
  const prevSlideRef = useRef(-1);

  const totalDuration = slideDurations.reduce((sum, d) => sum + d, 0);

  // Preload images
  useEffect(() => {
    let loaded = 0;
    const imageSlides = SLIDES.filter(s => s.type === "image");
    if (imageSlides.length === 0) { setImagesLoaded(true); return; }
    imageSlides.forEach(s => {
      const img = new Image();
      img.onload = img.onerror = () => { loaded++; if (loaded >= imageSlides.length) setImagesLoaded(true); };
      img.src = s.imageSrc;
    });
  }, []);

  // Generate & load voiceover audio, then adapt slide durations to actual audio lengths
  const loadAudio = useCallback(async () => {
    setAudioLoading(true);
    setAudioError(null);
    try {
      await fetch(`${API}/api/voiceover/generate`, { method: "POST" });
      const statusRes = await fetch(`${API}/api/voiceover/status`);
      const statusData = await statusRes.json();
      if (!statusData.ready) {
        setAudioError("Certains fichiers audio n'ont pas pu \u00eatre g\u00e9n\u00e9r\u00e9s.");
        setAudioLoading(false);
        return;
      }

      const PAUSE_AFTER_VOICEOVER = 4000; // 4s pause after voiceover ends
      const newDurations = [...SLIDES.map(s => s.duration)];
      const loadPromises = SLIDES.map((slide, idx) => new Promise((resolve) => {
        const audio = new Audio(`${API}/api/voiceover/audio/${slide.id}`);
        audio.preload = "auto";
        slideAudioRefs.current[slide.id] = audio;

        const onMeta = () => {
          if (audio.duration && isFinite(audio.duration)) {
            const audioDurationMs = audio.duration * 1000;
            // Slide must be at least as long as audio + pause
            newDurations[idx] = Math.max(slide.duration, audioDurationMs + PAUSE_AFTER_VOICEOVER);
          }
          audio.removeEventListener("loadedmetadata", onMeta);
          resolve();
        };
        audio.addEventListener("loadedmetadata", onMeta);
        // Fallback timeout if metadata never loads
        setTimeout(() => { audio.removeEventListener("loadedmetadata", onMeta); resolve(); }, 8000);
      }));

      await Promise.all(loadPromises);
      setSlideDurations(newDurations);
      setAudioReady(true);
    } catch (err) {
      
      setAudioError("Erreur lors du chargement audio.");
    }
    setAudioLoading(false);
  }, []);

  useEffect(() => {
    loadAudio();
    const timeout = setTimeout(() => {
      if (!autoStartDone.current && autoPlay) {
        autoStartDone.current = true;
        startTimeRef.current = Date.now();
        setIsPlaying(true);
      }
    }, 15000);
    return () => {
      clearTimeout(timeout);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const audioRefs = slideAudioRefs.current;
      const bgmNodes = bgmNodesRef.current;
      const bgmCtx = bgmCtxRef.current;
      Object.values(audioRefs).forEach(a => { try { a.pause(); a.src = ""; } catch {} });
      if (bgmNodes) {
        bgmNodes.stopArpeggio();
        bgmNodes.oscillators.forEach(o => { try { o.stop(); } catch {} });
      }
      if (bgmCtx) { try { bgmCtx.close(); } catch {} }
    };
  }, [loadAudio, autoPlay]);

  // Auto-start once audio + images are ready
  useEffect(() => {
    if (autoStartDone.current) return;
    if (autoPlay && (audioReady || audioError) && imagesLoaded) {
      autoStartDone.current = true;
      startTimeRef.current = Date.now();
      setIsPlaying(true);
    }
  }, [audioReady, audioError, autoPlay, imagesLoaded]);

  // Initialize background music - MUST be called from user gesture for browser policy
  const initBGM = useCallback(() => {
    if (bgmCtxRef.current) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const bgm = createBackgroundMusic(ctx);
      bgm.output.connect(ctx.destination);
      bgm.oscillators.forEach(o => o.start());
      bgm.startArpeggio();
      bgmCtxRef.current = ctx;
      bgmNodesRef.current = bgm;
    } catch (e) { }
  }, []);

  // Start/stop background music with Web Audio API
  useEffect(() => {
    if (isPlaying && !bgmCtxRef.current) {
      // Fallback: try creating from useEffect (may not work due to browser policy)
      initBGM();
    }
    if (bgmCtxRef.current) {
      if (isPlaying && !isMuted) {
        bgmCtxRef.current.resume().catch(() => {});
      } else if (!isPlaying || isMuted) {
        bgmCtxRef.current.suspend().catch(() => {});
      }
    }
  }, [isPlaying, isMuted, initBGM]);

  // Sync voiceover with slide transitions - with pauses between slides
  useEffect(() => {
    if (!audioReady || !isPlaying) return;
    const prevId = prevSlideRef.current >= 0 ? SLIDES[prevSlideRef.current]?.id : null;
    const currentId = SLIDES[currentSlide]?.id;

    // Fade out previous slide audio
    if (prevId && prevId !== currentId && slideAudioRefs.current[prevId]) {
      const pa = slideAudioRefs.current[prevId];
      const fi = setInterval(() => {
        if (pa.volume > 0.05) { pa.volume = Math.max(0, pa.volume - 0.06); }
        else { pa.pause(); pa.currentTime = 0; pa.volume = 1; clearInterval(fi); }
      }, 40);
    }

    // Start current slide audio with minimal delay
    if (currentId && slideAudioRefs.current[currentId]) {
      const a = slideAudioRefs.current[currentId];
      a.muted = isMuted;
      if (a.paused || a.currentTime === 0) {
        setTimeout(() => {
          a.volume = 0;
          a.play().catch(() => {});
          // Fast fade in over 300ms
          const fi = setInterval(() => {
            if (a.volume < 0.92) { a.volume = Math.min(1, a.volume + 0.12); }
            else { a.volume = 1; clearInterval(fi); }
          }, 30);
        }, 200); // 200ms delay (just enough for visual transition)
      }
    }

    prevSlideRef.current = currentSlide;

    // Switch background music to Phase 2 (energetic) when reaching the solution slide
    if (currentSlide >= 4 && bgmNodesRef.current?.switchToPhase2) {
      bgmNodesRef.current.switchToPhase2();
    }
  }, [currentSlide, isPlaying, isMuted, audioReady]);

  // Pause all audio when video pauses
  useEffect(() => {
    if (!isPlaying && audioReady) {
      Object.values(slideAudioRefs.current).forEach(a => { try { a.pause(); } catch(e){} });
    }
  }, [isPlaying, audioReady]);

  // Mute/unmute voiceover
  useEffect(() => {
    if (!audioReady) return;
    Object.values(slideAudioRefs.current).forEach(a => { a.muted = isMuted; });
  }, [isMuted, audioReady]);

  const tick = useCallback(() => {
    if (!startTimeRef.current || !isPlaying) return;
    const elapsed = Date.now() - startTimeRef.current;
    let acc = 0;
    for (let i = 0; i < SLIDES.length; i++) {
      const dur = slideDurations[i] || SLIDES[i].duration;
      if (elapsed < acc + dur) { setCurrentSlide(i); setProgress((elapsed - acc) / dur); break; }
      acc += dur;
      if (i === SLIDES.length - 1 && elapsed >= acc) { setCurrentSlide(SLIDES.length - 1); setProgress(1); setIsPlaying(false); return; }
    }
    animRef.current = requestAnimationFrame(tick);
  }, [isPlaying, slideDurations]);

  useEffect(() => {
    if (isPlaying) { if (!startTimeRef.current) startTimeRef.current = Date.now(); animRef.current = requestAnimationFrame(tick); }
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [isPlaying, tick]);

  const togglePlay = () => {
    // Initialize BGM from user gesture context (required by browser policy)
    initBGM();
    if (!isPlaying && !startTimeRef.current) { startTimeRef.current = Date.now(); }
    else if (!isPlaying && startTimeRef.current) {
      let acc = 0;
      for (let i = 0; i < currentSlide; i++) acc += slideDurations[i] || SLIDES[i].duration;
      acc += progress * (slideDurations[currentSlide] || SLIDES[currentSlide].duration);
      startTimeRef.current = Date.now() - acc;
    }
    setIsPlaying(!isPlaying);
  };

  const restart = () => {
    Object.values(slideAudioRefs.current).forEach(a => { try { a.pause(); a.currentTime = 0; a.volume = 1; } catch(e){} });
    // Reset background music
    if (bgmNodesRef.current) { bgmNodesRef.current.stopArpeggio(); bgmNodesRef.current.oscillators.forEach(o => { try { o.stop(); } catch(e){} }); }
    if (bgmCtxRef.current) { try { bgmCtxRef.current.close(); } catch(e){} }
    bgmCtxRef.current = null;
    bgmNodesRef.current = null;
    // Re-init BGM from user gesture context
    initBGM();
    prevSlideRef.current = -1;
    startTimeRef.current = Date.now();
    setCurrentSlide(0);
    setProgress(0);
    setIsPlaying(true);
  };

  // Seek to a specific position (0-1 ratio) when clicking on the progress bar
  const seekTo = (e) => {
    const bar = e.currentTarget;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const targetMs = ratio * totalDuration;

    // Stop all current voiceovers
    Object.values(slideAudioRefs.current).forEach(a => { try { a.pause(); a.currentTime = 0; a.volume = 1; } catch(e2){} });

    // Find the target slide and progress within it
    let acc = 0;
    for (let i = 0; i < SLIDES.length; i++) {
      const dur = slideDurations[i] || SLIDES[i].duration;
      if (targetMs < acc + dur) {
        setCurrentSlide(i);
        setProgress((targetMs - acc) / dur);
        prevSlideRef.current = -1; // Force voiceover re-trigger
        break;
      }
      acc += dur;
    }

    // Adjust startTime so the tick function continues from this position
    startTimeRef.current = Date.now() - targetMs;

    if (!isPlaying) {
      initBGM();
      setIsPlaying(true);
    }
  };

  // Server-side video generation (high-quality MP4)
  const handleServerRender = async () => {
    try {
      setServerRender({ status: "generating", progress: 5, phase: "D\u00e9marrage..." });
      const startRes = await fetch(`${API}/api/video/render`, { method: "POST" });
      const startData = await startRes.json();

      if (startData.status === "cached") {
        setServerRender({ status: "done", progress: 100, phase: "Termin\u00e9 !" });
        return;
      }

      // Poll for status
      const poll = async () => {
        for (let i = 0; i < 300; i++) { // Max 10 minutes
          await new Promise(r => setTimeout(r, 2000));
          const res = await fetch(`${API}/api/video/render-status`);
          const data = await res.json();
          setServerRender(data);
          if (data.status === "done" || data.status === "error") return data;
        }
        return { status: "error", error: "Timeout" };
      };
      await poll();
    } catch (e) {
      setServerRender({ status: "error", progress: 0, phase: "", error: e.message });
    }
  };

  if (!isVisible) return null;
  const slide = SLIDES[currentSlide];
  const curDur = slideDurations[currentSlide] || SLIDES[currentSlide].duration;
  const gp = (() => { let a = 0; for (let i = 0; i < currentSlide; i++) a += slideDurations[i] || SLIDES[i].duration; a += progress * curDur; return a / totalDuration; })();
  const el = Math.floor(gp * totalDuration / 1000);
  const ts = Math.floor(totalDuration / 1000);
  const timeStr = `${Math.floor(el / 60)}:${String(el % 60).padStart(2, "0")} / ${Math.floor(ts / 60)}:${String(ts % 60).padStart(2, "0")}`;
  const containerClass = inline ? "relative w-full aspect-video rounded-2xl overflow-hidden bg-slate-900 shadow-2xl" : "fixed inset-0 z-[100] bg-black flex items-center justify-center";

  // Smooth fade-out: start 3 seconds before slide end
  const msRemaining = curDur * (1 - progress);
  const fadeOutAtEnd = msRemaining < 3000 ? Math.max(0, msRemaining / 3000) : 1;

  return (
    <div className={containerClass} data-testid="presentation-video" onClick={() => { initBGM(); if (bgmCtxRef.current && isPlaying && !isMuted) bgmCtxRef.current.resume().catch(() => {}); }}>
      <div className={`${inline ? "w-full h-full" : "w-full h-full md:w-[90vw] md:h-[80vh] md:max-w-6xl md:rounded-2xl"} relative overflow-hidden bg-slate-900`}>
        {/* Backgrounds with crossfade */}
        {SLIDES.map((s, i) => s.type === "image" && (
          <div key={s.id} className={`absolute inset-0 transition-opacity duration-1500 ${i === currentSlide ? "opacity-100" : "opacity-0"}`}
            style={{ transitionDuration: "1500ms" }}>
            <img src={s.imageSrc} alt="" className="w-full h-full object-cover" loading="eager" />
          </div>
        ))}
        {SLIDES.map((s, i) => s.type === "text" && (
          <div key={s.id} className={`absolute inset-0 ${i === currentSlide ? "opacity-100" : "opacity-0"}`}
            style={{ background: s.bg, transition: "opacity 1500ms ease-in-out" }} />
        ))}

        {/* Content with fade-out at slide end */}
        <div className="absolute inset-0 z-10" style={{ opacity: fadeOutAtEnd, transition: "opacity 300ms ease" }}>
          {slide.content(progress)}
        </div>

        {/* Audio loading */}
        {audioLoading && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 bg-black/60 backdrop-blur-sm text-white px-4 py-2 rounded-full flex items-center gap-2 text-xs font-medium" data-testid="audio-loading">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />Pr&eacute;paration de la voix off...
          </div>
        )}
        {audioReady && !isPlaying && currentSlide === 0 && progress === 0 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 bg-emerald-500/80 backdrop-blur-sm text-white px-4 py-2 rounded-full flex items-center gap-2 text-xs font-medium" data-testid="audio-ready-badge">
            <Mic className="w-3.5 h-3.5" />Voix off et musique activ&eacute;es
          </div>
        )}
        {audioError && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 bg-red-500/80 backdrop-blur-sm text-white px-4 py-2 rounded-full flex items-center gap-2 text-xs font-medium" data-testid="audio-error">
            {audioError}<button onClick={loadAudio} className="ml-1 underline">R&eacute;essayer</button>
          </div>
        )}

        {/* Video recording progress overlay */}
        {isRecording && recordProgress && (
          <div className="absolute inset-0 z-50 bg-black/80 flex flex-col items-center justify-center">
            <Loader2 className="w-12 h-12 text-sky-400 animate-spin mb-4" />
            <p className="text-white text-lg font-semibold mb-2">{recordProgress.phase}</p>
            <div className="w-64 h-2 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-sky-400 rounded-full transition-all duration-500" style={{ width: `${recordProgress.pct}%` }} />
            </div>
            <p className="text-white/60 text-sm mt-2">{Math.round(recordProgress.pct)}%</p>
          </div>
        )}

        {/* Controls */}
        <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/70 to-transparent p-4">
          <div className="w-full h-2 bg-white/20 rounded-full mb-3 cursor-pointer group hover:h-3 transition-all relative" data-testid="video-progress" onClick={seekTo}>
            <div className="h-full bg-gradient-to-r from-sky-400 to-emerald-400 rounded-full transition-all duration-100 pointer-events-none" style={{ width: `${gp * 100}%` }} />
            <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{ left: `calc(${gp * 100}% - 6px)` }} />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={togglePlay} data-testid="video-play-btn" className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center hover:bg-white/30 transition-all">
                {isPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
              </button>
              <button onClick={() => setIsMuted(!isMuted)} data-testid="video-mute-btn" className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center hover:bg-white/30 transition-all">
                {isMuted ? <VolumeX className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-white" />}
              </button>
              <span className="text-xs text-white/60 font-mono">{timeStr}</span>
              {audioReady && <span className="text-[10px] text-emerald-400/80 flex items-center gap-1"><Mic className="w-3 h-3" />Voix off</span>}
              {!isPlaying && currentSlide === SLIDES.length - 1 && (
                <button onClick={restart} className="text-xs text-white/70 hover:text-white flex items-center gap-1"><RotateCcw className="w-3 h-3" />Rejouer</button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="flex gap-1.5">{SLIDES.map((_, i) => (<div key={i} className={`w-1.5 h-1.5 rounded-full transition-all ${i === currentSlide ? "bg-sky-400 w-4" : i < currentSlide ? "bg-white/50" : "bg-white/20"}`} />))}</div>
              {showDownload && (
                <>
                  {serverRender.status === "done" ? (
                    <a href={`${API}/api/video/download`} download="CMES-LOG_Presentation.mp4" data-testid="video-download-link"
                      className="h-10 px-4 rounded-full bg-emerald-500/60 backdrop-blur flex items-center justify-center gap-2 hover:bg-emerald-500/80 transition-all text-white text-xs font-semibold">
                      <Download className="w-4 h-4" />T&eacute;l&eacute;charger MP4
                    </a>
                  ) : serverRender.status === "generating" ? (
                    <div className="h-10 px-4 rounded-full bg-sky-500/40 backdrop-blur flex items-center gap-2 text-white text-xs">
                      <Loader2 className="w-4 h-4 animate-spin" />{serverRender.phase} {serverRender.progress}%
                    </div>
                  ) : serverRender.status === "error" ? (
                    <button onClick={handleServerRender} data-testid="video-download-btn"
                      className="h-10 px-4 rounded-full bg-red-500/40 backdrop-blur flex items-center gap-2 hover:bg-red-500/60 transition-all text-white text-xs">
                      <Video className="w-4 h-4" />R&eacute;essayer
                    </button>
                  ) : (
                    <button onClick={handleServerRender} data-testid="video-download-btn"
                      className="h-10 px-4 rounded-full bg-emerald-500/40 backdrop-blur flex items-center justify-center gap-2 hover:bg-emerald-500/60 transition-all text-white text-xs font-semibold">
                      <Video className="w-4 h-4" />G&eacute;n&eacute;rer la vid&eacute;o HD
                    </button>
                  )}
                </>
              )}
              {onClose && (
                <button onClick={() => { setIsVisible(false); onClose?.(); }} data-testid="video-close-btn" className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center hover:bg-white/30 transition-all">
                  <X className="w-5 h-5 text-white" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
